import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { AutenticacaoService } from '../autenticacao/autenticacao.service';
import { catchError, tap, concatMap, switchMap } from 'rxjs/operators';
import { ToastService } from '../libs/toast/toast.service';
import { NetworkPermissionService } from '../core/network-permission.service';
import { InternetConnectionService } from '../core/internet-connection.service';
import { AuthAlertService } from '../core/auth-alert.service';
import * as Sentry from '@sentry/angular';
import { UsuarioService } from '../autenticacao/usuario.service';

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {

  constructor(private autenticacaoService: AutenticacaoService,
    private alert: AuthAlertService,
    private toastService: ToastService,
    private networkPermission: NetworkPermissionService,
    private internetConnection: InternetConnectionService,
    private user: UsuarioService
  ) { }

  inserirHeaderAuthorization(req: HttpRequest<any>): HttpRequest<any> {
    let dupReq: HttpRequest<any>;
    let token = this.autenticacaoService.consultarAutenticacao()?.token.token;

    if (token)
      dupReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
    else
      dupReq = req;

    return dupReq;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let dupReq: HttpRequest<any>;

    if (req.headers.get("Content-Type") === null)
      dupReq = req.clone();
    //dupReq = req.clone({ headers: req.headers.set('Content-Type', `application/json`) });
    else if (req.headers.get("Content-Type") === 'null')
      dupReq = req.clone({ headers: req.headers.delete('Content-Type') });
    else
      dupReq = req.clone();

    return next.handle(this.inserirHeaderAuthorization(dupReq)).pipe(
      catchError(err => this.handleHttpError(err, dupReq))
    );
  }

  private handleHttpError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    console.log(`Interceptor - Error ${error.status}`, error);
    (error as any)._handled = true;

    // Se o erro for um Blob, precisamos processá-lo de forma assíncrona
    if (error.error instanceof Blob) {
      return from(this.parseBlobError(error)).pipe(
        switchMap((parsedError: HttpErrorResponse) => {
          return this.routeError(parsedError, request);
        })
      );
    }

    return this.routeError(error, request);
  }

  private routeError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    // Erros do próprio SMOAssinador. Tratado antes dos casos genéricos abaixo para não cair
    // no toast genérico de mensagem nem no alerta de "sem permissão" em caso de 401.
    if (this.isSMOAssinadorRequest(request)) {
      // Negócio: Codigo/Mensagem tipados (ver SMOAssinador.Excecoes.ExcecaoBase).
      if (this.isSMOAssinadorBusinessError(error)) {
        return this.handleSMOAssinadorBusinessError(error);
      }

      // Exceção não tratada no SMOAssinador (sem Codigo/Mensagem tipados).
      if (error.status === 500) {
        return this.handleSMOAssinadorInternalError(error);
      }
    }

    // Erros com mensagem customizada do servidor (>=400 && !401 && !403)
    if (this.isServerErrorWithMessage(error)) {
      return this.handleServerErrorWithMessage(error);
    }

    // Erros de autenticação/autorização (401, 403)
    if (this.isAuthError(error)) {
      return this.handleAuthError(error);
    }

    // Erros de rede/conexão (0, 504)
    if (this.isNetworkError(error)) {
      return this.handleNetworkError(error, request);
    }

    // Outros erros do servidor (>= 400) sem mensagem customizada
    if (this.isServerErrorWithoutMessage(error)) {
      return this.handleServerErrorWithoutMessage(error, request);
    }

    // Qualquer outro erro
    return this.handleUnknownError(error);
  }

  private async parseBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    try {
      const blob = error.error as Blob;
      const text = await blob.text();
      const json = JSON.parse(text);

      // Modifica o error.error do objeto original ao invés de criar um novo
      // Isso preserva o stack trace e todas as propriedades originais
      (error as any).error = json;

      return error;
    } catch (e) {
      // Se falhar ao parsear, retorna o erro original
      console.error('Failed to parse blob error:', e);
      return error;
    }
  }

  /**
   * Mensagens amigáveis por AppExcecaoCodigo do SMOAssinador (ver
   * SMOAssinador.Excecoes.AppExcecaoCodigo), para não depender apenas da Mensagem crua da
   * exceção .NET, que é técnica e pouco explicativa para o usuário final.
   */
  private readonly MENSAGENS_SMOASSINADOR: { [codigo: number]: string } = {
    1000: 'O certificado selecionado para assinatura é inválido ou expirou. Selecione outro certificado e tente novamente.',
    1010: 'Não foi possível carregar a página do documento no SMOAssinador. Tente novamente; se persistir, contate o suporte.',
    1020: 'Ocorreu um erro ao assinar o documento no SMOAssinador. Tente novamente; se persistir, contate o suporte.',
    1021: 'A operação de assinatura foi cancelada no SMOAssinador.'
  };

  /**
   * O SMOAssinador serializa o RetornoDto em PascalCase (Codigo/Mensagem), diferente da API
   * principal (camelCase) — por isso lê os dois formatos.
   */
  private extrairErroSMOAssinador(error: HttpErrorResponse): { codigo?: number; mensagem?: string } | null {
    const corpo = error.error;

    if (!corpo || typeof corpo !== 'object') {
      return null;
    }

    const codigo = corpo.codigo ?? corpo.Codigo;

    if (typeof codigo !== 'number') {
      return null;
    }

    return { codigo, mensagem: corpo.mensagem ?? corpo.Mensagem };
  }

  private isSMOAssinadorBusinessError(error: HttpErrorResponse): boolean {
    // status > 0: distingue de falhas de conexão/permissão de rede local (sem corpo de
    // resposta), que continuam sendo tratadas como "SMOAssinador não está respondendo".
    return error.status > 0 && this.extrairErroSMOAssinador(error) !== null;
  }

  private handleSMOAssinadorBusinessError(error: HttpErrorResponse): Observable<never> {
    const erro = this.extrairErroSMOAssinador(error);
    const mensagem = (erro?.codigo !== undefined && this.MENSAGENS_SMOASSINADOR[erro.codigo])
      || erro?.mensagem
      || 'Ocorreu um problema na operação de assinatura pelo SMOAssinador. Se persistir, contate o suporte.';

    this.toastService.showError(mensagem);
    return throwError(() => error);
  }

  private handleSMOAssinadorInternalError(error: HttpErrorResponse): Observable<never> {
    this.toastService.showError(
      'Ocorreu um erro interno no SMOAssinador. Tente novamente; se o problema persistir, contate o suporte.'
    );
    this.captureToSentry(error);
    return throwError(() => error);
  }

  private isServerErrorWithMessage(error: HttpErrorResponse): boolean {
    let errorObj = error?.error;

    return !!(errorObj?.mensagem || errorObj?.mensagens) && (this.isAuthError(error) === false);
  }

  private isAuthError(error: HttpErrorResponse): boolean {
    return error.status === 403 || error.status === 401;
  }

  private isNetworkError(error: HttpErrorResponse): boolean {
    return error.status === 0 || !error.status || error.status === 504;
  }

  private isServerErrorWithoutMessage(error: HttpErrorResponse): boolean {
    return error.status >= 400;
  }

  private handleServerErrorWithMessage(error: HttpErrorResponse): Observable<never> {
    let errorObj = error?.error;

    let mensagem = errorObj.mensagem || (errorObj.mensagens && errorObj.mensagens.join('<br/>'));

    this.toastService.showWarning(mensagem);
    return throwError(() => error);
  }

  private handleAuthError(error: HttpErrorResponse): Observable<never> {
    this.alert.showNoPermissionAction();
    return throwError(() => error);
  }

  private handleNetworkError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    return from(this.networkPermission.isLocalNetworkAccessError(request.url, error.status)).pipe(
      switchMap((isLNAError: boolean) => {
        if (isLNAError) {
          return this.handleLocalNetworkAccessError(error, request);
        }
        return this.handleConnectionError(error, request);
      })
    );
  }

  private handleLocalNetworkAccessError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    console.error('Local Network Access permission denied');

    if (this.isSMOLinkRequest(request)) {
      // Quando o SMOLink não está em execução o navegador não apresenta o prompt de permissão.
      this.toastService.showError(
        'O SMOLink não está respondendo. Verifique se o aplicativo SMOLink está em execução ao lado do relógio do sistema ' +
        'e se foi permitido acesso à rede local nas configurações do navegador.'
      );
      return throwError(() => error);
    }

    if (this.isSMOAssinadorRequest(request)) {
      this.toastService.showError(
        'O SMOAssinador não está respondendo. Verifique se o aplicativo SMOAssinador está em execução ao lado do relógio do sistema ' +
        'e se foi permitido acesso à rede local nas configurações do navegador.'
      );
      return throwError(() => error);
    }

    this.toastService.showError(
      'Acesso à rede local negado. Por favor, permita o acesso à rede local nas configurações do navegador.'
    );

    return throwError(() => error);
  }

  private handleConnectionError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    // Verifica se é uma requisição para o SMOLink local
    if (this.isSMOLinkRequest(request)) {
      return this.handleSMOLinkError(error);
    }

    if (this.isSMOAssinadorRequest(request)) {
      return this.handleSMOAssinadorError(error);
    }

    return this.internetConnection.checkInternetConnection().pipe(
      switchMap((hasInternet: boolean) => {
        if (!hasInternet) {
          return this.handleNoInternetError(error);
        }
        return this.handleServerUnavailableError(error);
      })
    );
  }

  private isSMOLinkRequest(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
    const hasSMOLink = url.includes('smolink') || url.includes(':45675');
    return isLocalhost && hasSMOLink;
  }

  private isSMOAssinadorRequest(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
    const hasSMOAssinador = url.includes('smoassinador') || url.includes(':45684');
    return isLocalhost && hasSMOAssinador;
  }

  private handleSMOLinkError(error: HttpErrorResponse): Observable<never> {
    console.error('SMOLink application not responding');
    this.toastService.showError(
      'O SMOLink não está respondendo. Verifique se o aplicativo SMOLink está em execução ao lado do relógio do sistema.'
    );
    return throwError(() => error);
  }

  private handleSMOAssinadorError(error: HttpErrorResponse): Observable<never> {
    console.error('SMOAssinador application not responding');
    this.toastService.showError(
      'O SMOAssinador não está respondendo. Verifique se o aplicativo SMOAssinador está em execução ao lado do relógio do sistema.'
    );
    return throwError(() => error);
  }

  private handleNoInternetError(error: HttpErrorResponse): Observable<never> {
    console.error('No internet connection detected');
    this.toastService.showError(
      'Sem conexão com a internet. Verifique sua conexão e tente novamente.'
    );

    return throwError(() => error);
  }

  private handleServerUnavailableError(error: HttpErrorResponse): Observable<never> {
    this.toastService.showError(
      'Houve uma instabilidade ao processar a solicitação. Tenta novamente em alguns instantes. Se continuar ocorrendo, entra em contato com o suporte.'
    );
    this.captureToSentry(error);
    return throwError(() => error);
  }

  /**
   * Erros do servidor sem mensagem customizada (>=400). Retorna mensagem genérica.
   * @param error
   * @returns 
   */
  private handleServerErrorWithoutMessage(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    if (error.status === 400 && this.isSMOLinkRequest(request)) {
      // SMOLink retorna 400 sem mensagem em alguns casos.
      return EMPTY;
    }

    this.toastService.showError(
      'Ocorreu um erro inesperado no servidor (código: 500X). Tenta novamente em alguns instantes. Se persistir, entra em contato com o suporte.'
    );
    this.captureToSentry(error);
    return throwError(() => error);
  }

  /**
   * Erros desconhecidos (não tratados). Retorna mensagem genérica.
   * @param error
   * @returns
   */
  private handleUnknownError(error: HttpErrorResponse): Observable<never> {
    this.toastService.showError(
      'Ocorreu um erro inesperado (código: 500UK). Se o problema persistir, por favor, contate o suporte.'
    );
    this.captureToSentry(error);
    return throwError(() => error);
  }


  captureToSentry(error: HttpErrorResponse, req?: HttpRequest<any>) {
    // --- Enviar para o Sentry ---
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        type: 'http-error',
        status: error.status.toString(),
      },
      user: {
        id: this.user?.consultarUsuario()?.id,
        username: this.user?.consultarUsuario()?.username,
      },
      extra: {
        url: req?.url,
        method: req?.method,
        status: error.status,
        message: error.message,
        error: error.error,
        name: error.name,
        timestamp: new Date().toISOString()
      }
    });
  }
}
